import { Box, List, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import CalendarDaysIcon from "@/modules/common/components/ui-kit/icons/calendar-days-icon";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import MeetingItem from "@/modules/meetings/components/meetings/meeting-item";
import { getTasksDashboard } from "@/modules/tasks/api";
import { TasksResponse } from "@/modules/tasks/dtos";
import { SystemTaskTypes } from "@/modules/tasks/enums";
import { useMeetingCompletion } from "@/modules/tasks/hooks/use-meeting-completion.hook";
import { MeetingCompletionModal } from "@/modules/tasks/components/meeting-completion-modal";
interface MeetingsProps {
  onMeetingsStatusChange?: (hasMeetings: boolean) => void;
}

export default function Meetings({ onMeetingsStatusChange }: MeetingsProps) {
  const screen = useScreenSize();

  // Use the meeting completion hook
  const { state, handlers } = useMeetingCompletion();

  const filters = {
    types: SystemTaskTypes.Meeting_subordinate,
    onlyU: true,
  };
  const pagination = { page: 1, pageSize: 10 };

  const {
    data: meetingsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["tasks-dashboard"],
    queryFn: () =>
      getTasksDashboard({
        filters,
        pagination,
      }),
  });

  const handleCheckboxChange = (meeting: TasksResponse) => {
    handlers.openModalForTask(meeting);
  };

  const meetings = meetingsData?.data?.data || [];

  // Notify parent component about meetings status
  useEffect(() => {
    if (!isLoading && !isError) {
      onMeetingsStatusChange?.(meetings.length > 0);
    }
  }, [meetings.length, isLoading, isError, onMeetingsStatusChange]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "40px",
        }}
      >
        <Typography>Loading meetings...</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "40px",
        }}
      >
        <Typography color="error">Error loading meetings</Typography>
      </Box>
    );
  }

  if (meetings.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "16px 8px",
          gap: 1,
        }}
      >
        <CalendarDaysIcon
          width={24}
          height={24}
          fill={nawyTheme.nawy.colors.semantic.layer.primaryInverse}
        />
        <Typography
          sx={{
            ...nawyTheme.nawy.typography.english.variants.headline.xs,
            color: nawyTheme.nawy.colors.semantic.text.primary,
          }}
        >
          No meetings yet
        </Typography>
        <Typography
          sx={{
            ...nawyTheme.nawy.typography.english.variants.body.md,
            color: nawyTheme.nawy.colors.semantic.text.primary,
          }}
        >
          You don't have any meetings yet.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <List
        sx={{
          padding: 0,
          overflowY: "auto",
          maxHeight: "700px",
          minHeight: screen.isMobile ? "100px" : undefined,
          height: screen.isMobile ? "65vh" : undefined,
        }}
      >
        {Array.isArray(meetings) &&
          meetings.map((meeting: TasksResponse) => (
            <MeetingItem
              key={meeting.uid}
              meeting={meeting}
              onCheckboxChange={handleCheckboxChange}
            />
          ))}
      </List>

      <MeetingCompletionModal
        open={state.isModalOpen}
        onClose={handlers.handleCloseModal}
        onConfirm={handlers.handleConfirmModal}
        task={state.selectedTask}
        confirmButtonText={state.confirmButtonText}
        isLoading={state.isLoading}
        hasIncompleteTasks={state.hasIncompleteTasks}
        tasksReadyForEvaluation={state.tasksReadyForEvaluation}
        markAsCompletedRef={state.markAsCompletedRef}
        onTasksChange={handlers.setMeetingTasks}
        onValidationChange={handlers.handleValidationChange}
        onTasksNeedEvaluation={handlers.handleTasksNeedEvaluation}
        onTasksReadyForEvaluation={handlers.handleTasksReadyForEvaluation}
      />
    </>
  );
}
